import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Priority } from '@/constants/priority'
import { listTagsWithRelationsQuery } from '@/queries/tags.query'
import { TagService } from '@/services/tag.service'
import { DotsHorizontalIcon } from '@radix-ui/react-icons'
import { IconTrash } from '@tabler/icons-react'
import { useQueryClient } from '@tanstack/react-query'
import { Row } from '@tanstack/react-table'
import { toast } from 'sonner'
import { useTags } from '../context/tags-context'
import { tagSchema } from '../schema'
import { priorities } from './columns'

interface DataTableRowActionsProps<TData> {
  row: Row<TData>
}

export function DataTableRowActions<TData>({
  row,
}: DataTableRowActionsProps<TData>) {
  const tag = tagSchema.parse(row.original)
  const queryClient = useQueryClient()

  const { setOpen, setCurrentRow } = useTags()

  async function handleChangePriority(value: Priority) {
    toast.promise(TagService.changePriority(tag.id, value), {
      loading: `Alterando prioridade da tag ${tag.name}...`,
      success: () => {
        queryClient.invalidateQueries(listTagsWithRelationsQuery)
        return `Prioridade da tag ${tag.name} alterada.`;
      },
      error: () => {
        queryClient.invalidateQueries(listTagsWithRelationsQuery)
        return `Erro ao alterar prioridade da tag ${tag.name}.`;
      },
    })
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant='ghost'
          className='flex h-8 w-8 p-0 data-[state=open]:bg-muted'
        >
          <DotsHorizontalIcon className='h-4 w-4' />
          <span className='sr-only'>Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='w-[160px]'>
        <DropdownMenuItem
          onClick={() => {
            setCurrentRow(tag)
            setOpen('update')
          }}
        >
          Editar
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className='data-[state=open]:text-white hover:text-white'>Prioridade</DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup 
              value={tag.priority} 
              onValueChange={(value) => handleChangePriority(value as Priority)}
            >
              {priorities.map((priority) => (
                <DropdownMenuRadioItem key={priority.value} value={priority.value}>
                  {priority.label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => {
            setCurrentRow(tag)
            setOpen('delete')
          }}
        >
          Remover
          <DropdownMenuShortcut>
            <IconTrash size={16} />
          </DropdownMenuShortcut>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
